import {Theme} from '@mui/material';
import {Localization} from '@mui/material/locale';
import {createTheme} from '@mui/material/styles';

import {red, purple, canvasGray, haidiBlue, haidiCoolGray, white, black, merieCoolGray, merieMetallicSeaweed} from './haidiColors';
import {coolGray} from '@wallaby-ui/merie/src/js/config/theme/merieColors';

export const themeVariables = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1366,
      xl: 1536,
    },
  },
  typography: {
    fontFamily: 'SourceSansPro-Regular',
    fontWeightExtraLight: 200,
    fontWeightSemiBold: 400,
    basicText: {
      fontFamily: 'SourceSansPro-Regular',
      fontSize: '14px',
      lineHeight: '22px',
    },
    basicTextBold: {
      fontFamily: 'SourceSansPro-Semibold',
      fontSize: '14px',
      lineHeight: '18px',
    },
    emptyValue: {
      fontFamily: 'SourceSansPro-Regular',
      fontSize: '14px',
      fontStyle: 'italic',
      lineHeight: '18px',
    },
    emptyValueBold: {
      fontFamily: 'SourceSansPro-Bold',
      fontSize: '14px',
      fontStyle: 'italic',
      lineHeight: '14px',
    },
    value: {
      fontFamily: 'SourceSansPro-Regular',
      fontSize: '14px',
      lineHeight: '18px',
    },
    valueBold: {
      fontFamily: 'SourceSansPro-Semibold',
      fontSize: '14px',
      lineHeight: '18px',
    },
    valueSmall: {
      fontFamily: 'SourceSansPro-Regular',
      fontSize: '12px',
      lineHeight: '12px',
    },
    label: {
      fontFamily: 'SourceSansPro-Semibold',
      fontSize: '14px',
      lineHeight: '18px',
    },
    labelBold: {
      fontFamily: 'SourceSansPro-Bold',
      fontSize: '14px',
      lineHeight: '18px',
    },
    sectionHeader: {
      fontFamily: 'SourceSansPro-Bold',
      fontSize: '14px',
      lineHeight: '18px',
    },
    cardHeader: {
      fontFamily: 'SourceSansPro-Bold',
      fontSize: '18px',
      lineHeight: '23px',
    },
    caption: {
      fontFamily: 'SourceSansPro-Regular',
      fontSize: '12px',
      lineHeight: '22px',
    },
    index: {
      fontFamily: 'SourceSansPro-Regular',
      fontSize: '10px',
      lineHeight: '13px',
    },
    chipButton: {
      fontFamily: 'SourceSansPro-Semibold',
      fontSize: '12px',
      lineHeight: '22px',
    },
    popper: {
      fontFamily: 'SourceSansPro-Regular',
      fontSize: '12px',
      lineHeight: '15px',
    },
    textButtonRegular: {
      fontFamily: 'SourceSansPro-Regular',
      fontSize: '14px',
      lineHeight: '22px',
    },
    textButton: {
      fontFamily: 'SourceSansPro-Semibold',
      fontSize: '14px',
      lineHeight: '22px',
    },
    patientCardName: {
      fontFamily: 'SourceSansPro-Bold',
      fontSize: '20px',
      lineHeight: '25px',
    },
    riskScoreValue: {
      fontFamily: 'SourceSansPro-Bold',
      fontSize: '34px',
      lineHeight: '26px',
    },
    riskScoreChange: {
      fontFamily: 'SourceSansPro-Bold',
      fontSize: '11px',
      lineHeight: '10px',
    },
    userName: {
      fontFamily: 'SourceSansPro-Regular',
      fontSize: '14px',
      lineHeight: '17.6px',
    },
    loginMenu: {
      fontFamily: 'SourceSansPro-Regular',
      fontSize: '14px',
      lineHeight: '18px',
    },
    hierarchyLabel: {
      fontFamily: 'SourceSansPro-Regular',
      fontSize: '14px',
      lineHeight: '18px',
    },
  },
  spacing: 4,
  borderRadius: '16px',
  direction: 'ltr',
  palette: {
    custom: {
      red,
      purple,
      theme: haidiBlue,
      themeGray: haidiCoolGray,
      haidiBlue,
      haidiCoolGray,
      coolGray: merieCoolGray,
      metallicSeaweed: merieMetallicSeaweed,
      white,
      black,

      navigationBox: haidiBlue['700'],
      navigationHeader: haidiCoolGray['700'],
      navigationHeaderIcon: haidiCoolGray['700'],
      navigationItemAvatar: haidiCoolGray['700'],
      navigationButton: haidiBlue['300'],
      navigationButtonHover: haidiBlue['500'],
      dividerBorderColor: haidiCoolGray['700'],
      userSection: haidiBlue['700'],
      userSectionHover: haidiBlue['900'],
      userSectionHoverText: haidiBlue['300'],
      userMenu: '#ffffff',
      userMenuRow: '#ffffff',
      userMenuRowHover: haidiCoolGray['200'],
      userMenuRowPressed: haidiCoolGray['200'],
      userMenuIcon: '#252d37',
      userMenuBorder: coolGray['100'],
      threeDotsColor: haidiCoolGray['500'],
      threeDotsColorHover: '#ffffff',
      removeItemIcon: '#94a0ab',
      fixedPaginationButtonBgColor: merieCoolGray['50'],
      fixedPaginationButtonIcon: haidiBlue['500'],
      fixedPaginationButtonIconBig: haidiBlue['700'],
      fixedPaginationButtonIconDisabled: merieCoolGray['500'],
      fixedPaginationButtonIconDisabledBig: haidiCoolGray['300'],
      fixedPaginationButtonIconBorder: haidiCoolGray['300'],
      headerLabel: haidiBlue['700'],

      dlHierarchyNodeItemText: haidiBlue['700'],
      dlHierarchyNodeItemTextFocus: white,
      dlHierarchyNodeItemTextFocusHover: white,

      dlHierarchyNodeItemHover: canvasGray,
      dlHierarchyNodeItemFocus: haidiBlue['500'],
      dlHierarchyNodeItemFocusHover: haidiBlue['600'],
      dlHierarchyNodeItemDisabledBackground: white,
      dlHierarchyNodeItemDisabled: haidiCoolGray['500'],
    },
  },
  components: {},
});

export const theme = createTheme(themeVariables, {
  components: {
    MuiSelect: {
      defaultProps: {
        variant: 'standard',
      },

      styleOverrides: {
        iconStandard: {
          right: 8,
          top: 12,
        },
        select: {
          padding: '8.5px 48px 8.5px 14px !important',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          'color': themeVariables.palette.custom.haidiCoolGray['800'],
          '&:active, &:visited, &:hover, &:focus': {
            backgroundColor: 'var(--dlAgGridColumnHover)',
            color: themeVariables.palette.custom.haidiCoolGray['800'],
          },
          '&:active:focus, &:active:hover': {
            backgroundColor: themeVariables.palette.custom.haidiBlue['600'],
            color: themeVariables.palette.custom.white,
            outline: 'none',
          },
          '&[aria-selected="true"]': {
            'backgroundColor': themeVariables.palette.custom.haidiBlue['500'],
            'color': themeVariables.palette.custom.white,
            '&:active, &:visited, &:focus': {
              backgroundColor: themeVariables.palette.custom.haidiBlue['500'],
              color: themeVariables.palette.custom.white,
            },
            '&:hover': {
              backgroundColor: themeVariables.palette.custom.haidiBlue['600'],
              color: themeVariables.palette.custom.white,
            },
          },
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        popupIndicator: {
          top: 0,
        },
        clearIndicator: {
          top: 0,
        },
      },
    },
    MuiInput: {
      defaultProps: {
        disableUnderline: true,
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          paddingTop: 5,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
      styleOverrides: {
        root: {
          ...themeVariables.typography.value,
          'color': haidiCoolGray['900'],
          '& label': {
            color: haidiCoolGray['900'],
          },
          '& label.Mui-focused': {
            color: haidiBlue['600'],
          },
          '& .MuiOutlinedInput-root': {
            ...themeVariables.typography.value,
            color: haidiCoolGray['900'],
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        sizeSmall: {
          marginTop: 0,
          lineHeight: '12px',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          // Set to 0 because it interferes with clickable area in TextField
          zIndex: 0,
        },
        sizeSmall: {
          paddingRight: 30,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        clickable: {
          ...themeVariables.typography.chipButton,
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
        disableFocusRipple: true,
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiSlider: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        markLabel: {
          top: '20px',
        },
        valueLabel: {
          top: '-10px',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTabs-flexContainer': {
            gap: '16px',
          },
          'button.Mui-selected': {
            'color': themeVariables.palette.custom.white,
            'backgroundColor': themeVariables.palette.custom.haidiBlue['500'],
            '& .MuiTab-iconWrapper': {
              color: themeVariables.palette.custom.white,
            },
            '&:hover': {
              backgroundColor: themeVariables.palette.custom.haidiBlue['600'],
            },
            '&:active': {
              backgroundColor: themeVariables.palette.custom.haidiBlue['700'],
            },
          },
          'button': {
            textTransform: 'none',
          },
        },
        indicator: {
          display: 'none',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          ...themeVariables.typography.cardHeader,
          'color': themeVariables.palette.custom.haidiCoolGray['800'],
          'gap': '8px',
          'borderRadius': '8px',
          'minHeight': 'auto',
          'padding': '8px 12px',
          '& .MuiTab-iconWrapper': {
            color: themeVariables.palette.custom.haidiBlue['300'],
          },
          '&:hover': {
            'backgroundColor': themeVariables.palette.custom.haidiBlue['600'],
            'color': themeVariables.palette.custom.white,
            '& .MuiTab-iconWrapper': {
              color: themeVariables.palette.custom.white,
            },
          },
          '&:active': {
            'backgroundColor': themeVariables.palette.custom.haidiBlue['700'],
            'color': themeVariables.palette.custom.white,
            '& .MuiTab-iconWrapper': {
              color: themeVariables.palette.custom.white,
            },
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          'color': themeVariables.palette.custom.haidiCoolGray['800'],
          '&.Mui-checked': {
            // This will apply only to checked state
            color: themeVariables.palette.custom.haidiBlue['500'],
          },
          '&:hover': {
            backgroundColor: themeVariables.palette.custom.haidiBlue['100'],
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          'margin': 0,
          '& .MuiRadio-root': {
            padding: 0,
          },
          '& .MuiFormControlLabel-label': {
            paddingLeft: '4px',
          },
        },
      },
    },
  },
});

export function dlMuiCreateThemeFunction(theme: Theme, localeData: Localization) {
  return createTheme(theme, localeData);
}
